/**
 * ReferralPage - Share referral link and track rewards
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Users, Gift, Copy, CheckCircle, Share2, 
  Twitter, Linkedin, Mail, MessageCircle, Loader2,
  DollarSign, UserPlus, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth?redirect=/referral');
      return;
    }
    fetchReferralStats();
  }, [user, token]);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (err) {
      console.error('Failed to fetch referral stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = referralData?.referral_link || 
    `${window.location.origin}/auth?mode=register&ref=${referralData?.referral_code || ''}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnTwitter = () => {
    const text = `I've been using TripleTC for AI tasks and it's amazing! Get 5 free tasks when you sign up:`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = "Check out TripleTC - AI Agent Marketplace";
    const body = `Hey!\n\nI've been using TripleTC for AI tasks like content writing, image generation, and building apps. It's super useful!\n\nYou get 5 free tasks when you sign up with my referral link:\n${referralLink}\n\nCheck it out!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Spread the Word
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Love TripleTC? Share it with friends and help them discover AI-powered productivity
          </p>
        </motion.div>

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-6 h-6 text-teal-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">1. Share Your Link</h3>
            <p className="text-zinc-400 text-sm">Copy your unique referral link and share it with friends</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">2. Friend Signs Up</h3>
            <p className="text-zinc-400 text-sm">They get 5 free tasks to try out the platform</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Gift className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">3. Everyone Wins</h3>
            <p className="text-zinc-400 text-sm">Help grow the TripleTC community together</p>
          </div>
        </motion.div>

        {/* Referral Link Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-teal-500/10 to-blue-500/10 border border-teal-500/30 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-xl font-bold text-white mb-4">Your Referral Link</h2>
          
          <div className="bg-zinc-900/80 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between gap-4">
              <code className="text-teal-400 text-sm break-all flex-1">
                {referralLink}
              </code>
              <Button
                onClick={copyLink}
                className={`flex-shrink-0 ${copied ? 'bg-green-500' : 'bg-teal-500 hover:bg-teal-600'}`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <span className="text-zinc-400">Your code:</span>
            <span className="text-white font-mono font-bold text-lg bg-zinc-800 px-3 py-1 rounded">
              {referralData?.referral_code || 'N/A'}
            </span>
          </div>

          {/* Share Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={shareOnTwitter}
              className="bg-[#1DA1F2] hover:bg-[#1a8cd8]"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Twitter
            </Button>
            <Button
              onClick={shareOnLinkedIn}
              className="bg-[#0A66C2] hover:bg-[#094d92]"
            >
              <Linkedin className="w-4 h-4 mr-2" />
              LinkedIn
            </Button>
            <Button
              onClick={shareViaEmail}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-teal-400" />
              <span className="text-zinc-400">Total Referrals</span>
            </div>
            <p className="text-3xl font-bold text-white">{referralData?.total_referrals || 0}</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span className="text-zinc-400">Credits Earned</span>
            </div>
            <p className="text-3xl font-bold text-green-400">
              ${referralData?.referral_credits_earned?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-zinc-400">Reward per Referral</span>
            </div>
            <p className="text-3xl font-bold text-white">${referralData?.reward_per_referral || 5}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
